/* ─────────────────────────────────────────────────────────
   Waiters – CRUD page
   • Filters by the real Waiter role _id (WAITERS_ROLE_ID)
   • Uses its own table (<DataTable />)
   • Shares drawer, filter & permission logic
────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Swal                    from 'sweetalert2';
import { useDispatch }         from 'react-redux';

import Breadcrumb              from '@/components/common/Breadcrumb';
import CustomDropdown          from '@/components/common/CustomDropdown';
import Card                    from '@/components/ui/cards/Card';
import CardHeader              from '@/components/ui/cards/CardHeader';
import PerPageSelection        from '@/components/ui/tables/PerPageSelection';
import PritntPdfTable          from '@/components/common/PritntPdfTable';
import PermissionCheck         from '@/components/common/PermissionCheck';

import validationErrors        from '@/lib/validationErrors';
import { cn }                  from '@/lib/utils';
import { getAdminService,
         deleteAdminService }  from '@/services/AdminServices';

/* shared components */
import DataTableFilter         from './component/DataTableFilter';
import AddEditDrawer           from './component/AddEditDrawer';
/* waiter-specific table */
import DataTable               from './component/DataTable';

/* REAL ObjectId of the “Waiter” role as seeded in DB ------------ */
const WAITERS_ROLE_ID = '685b1af7956beac77bebe6d1';

const WaitersPage = ({ pageTitle = 'Waiters' }) => {
  const dispatch = useDispatch();
  const { printTableRef, handleDownloadPdf } = PritntPdfTable('Waiters Data');

  const [dataSource,   setDataSource]   = useState([]);
  const [totalRecord,  setTotalRecord]  = useState(0);
  const [activeDrawer, setActiveDrawer] = useState(false);
  const [editDataId,   setEditDataId]   = useState('');
  const [isLoading,    setIsLoading]    = useState(false);
  const [filterOpen,   setFilterOpen]   = useState(false);
  const filterDivRef   = useRef(null);

  const [dataFilter, setDataFilter] = useState({
    search : '',
    page   : 1,
    limit  : 10,
    extra_filter: { role_id: WAITERS_ROLE_ID }, // ✅ only waiter role
  });

  const columns = [
    { name: 'Name',   isIncludePrint: true },
    { name: 'Email',  isIncludePrint: true },
    { name: 'Phone',  isIncludePrint: true },
    { name: 'Role',   isIncludePrint: true },
    { name: 'Status', isIncludePrint: true },
    { name: 'Salary', isIncludePrint: true },
    { name: 'Action', isIncludePrint: false },
  ];

  const fetchTableData = useCallback(() => {
    setIsLoading(true);
    dispatch(getAdminService(dataFilter))
      .then((res) => {
        const { data, total } = res.data.data;
        setDataSource(data);
        setTotalRecord(total);
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  }, [dispatch, dataFilter]);

  const handleDrawerOpen = (id = '') => {
    setEditDataId(id);
    setActiveDrawer(true);
    document.body.style.overflowY = 'hidden';
  };

  const handleDrawerClose = () => {
    setActiveDrawer(false);
    setEditDataId('');
    document.body.style.overflowY = '';
  };

  const onChangeFilter = (key, value) => {
    if (key === 'extra_filter') {
      setDataFilter((prev) => ({
        ...prev,
        extra_filter: { ...prev.extra_filter, ...value },
      }));
    } else {
      setDataFilter((prev) => ({ ...prev, [key]: value }));
    }
  };

  const toggleFilter = () => {
    if (!filterDivRef.current) return;
    setFilterOpen((o) => !o);
    filterDivRef.current.style.height = !filterOpen ? '100%' : '0px';
  };

  const deleteRecord = (id) => {
    Swal.fire({
      title: 'Delete this waiter?',
      text : 'This action is irreversible.',
      icon : 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
    }).then((result) => {
      if (!result.isConfirmed) return;

      setIsLoading(true);
      dispatch(deleteAdminService(id))
        .then(() => {
          Swal.fire('Deleted!', 'Waiter removed.', 'success');
          fetchTableData();
        })
        .catch(({ message, errorData }) => validationErrors(errorData, message))
        .finally(() => setIsLoading(false));
    });
  };

  useEffect(() => fetchTableData(), [fetchTableData]);

  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />

      <Card>
        <CardHeader>
          <h3 className="db-card-title">{pageTitle}</h3>

          <div className="db-card-filter">
            <div className="db-field-down-arrow">
              <PerPageSelection
                onChangeParPage={onChangeFilter}
                limit={dataFilter.limit}
              />
            </div>

            <button
              className="db-card-filter-btn table-filter-btn"
              onClick={toggleFilter}
            >
              <i className="lab lab-filter lab-font-size-14" />
              <span>Filter</span>
            </button>

            <CustomDropdown
              buttonText="Export"
              buttonIcon="lab-file-export"
              dropdownList={[{
                icon: 'lab-printer-line',
                text: 'Print',
                dropDownAction: handleDownloadPdf,
              }]}
            />

            <PermissionCheck permsission="create_employees">
              <button
                type="button"
                onClick={() => handleDrawerOpen('')}
                className="db-btn h-[37px] text-white bg-primary"
              >
                <i className="lab lab-add-circle-line" />
                Add Waiter
              </button>
            </PermissionCheck>
          </div>
        </CardHeader>

        {/* Filter accordion */}
        <div
          className={cn('table-filter-div', filterOpen && 'active')}
          ref={filterDivRef}
          style={{ height: filterOpen ? '100%' : '0px' }}
        >
          <DataTableFilter onChangeFilter={onChangeFilter} />
        </div>

        {/* Data Table */}
        <DataTable
          isLoading        ={isLoading}
          tableColumn      ={columns}
          dataSource       ={dataSource}
          totalRecord      ={totalRecord}
          dataFilter       ={dataFilter}
          onChangePage     ={onChangeFilter}
          handleDrawerOpen ={handleDrawerOpen}
          deleteRecord     ={deleteRecord}
        />

        {/* Drawer */}
        <div ref={printTableRef}>
          <AddEditDrawer
            titleText        ={pageTitle}
            getTableData     ={fetchTableData}
            activeDrawer     ={activeDrawer}
            setActiveDrawer  ={handleDrawerClose}
            editDataId       ={editDataId}
            waiterRoleId     ={WAITERS_ROLE_ID}   // ✅ lock role in drawer
          />
        </div>
      </Card>
    </>
  );
};

export default WaitersPage;
